'use client';

import { flexRender } from '@tanstack/react-table';
import { cj, cm } from 'src/utils/classNames';
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableHeadRow,
	TableRow,
} from 'src/components/primitives/Table/Table';
import {
	PINNED_COLUMN_CLASSES,
	TABLE_DEFAULT_EMPTY_MESSAGE,
} from 'src/components/primitives/Table/consts';
import Paginator from 'src/components/Paginator';
import { useDataTableSetup } from 'src/components/primitives/Table/useDataTableSetup';
import { DataTableProps } from 'src/components/primitives/Table/types';

export function DataTable<T>({
	columns,
	data,
	onRowClick,
	emptyMessage = TABLE_DEFAULT_EMPTY_MESSAGE,
	emptyClassName,
	className,
	meta,
	columnPinning,
	footer,
	pagination,
	dataTestId,
}: DataTableProps<T>) {
	const {
		table,
		containerRef,
		getColumnStyle,
		getStuckClass,
		showPaginator,
		isEmpty,
	} = useDataTableSetup({ data, columns, meta, columnPinning, pagination });

	return (
		<div className="flex h-full flex-col">
			<div className="min-h-0 flex-1 overflow-auto">
				<Table
					className={cm(
						isEmpty && ['h-full', emptyClassName],
						!showPaginator && 'overflow-hidden rounded-b-sm',
						className
					)}
					containerRef={containerRef}
					dataTestId={dataTestId}
				>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableHeadRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const isPinned = header.column.getIsPinned();
									const headerCellClassName =
										header.column.columnDef.meta?.headerCellClassName;
									return (
										<TableHead
											key={header.id}
											style={getColumnStyle(header.column)}
											className={cm(
												'whitespace-nowrap capitalize',
												isPinned && PINNED_COLUMN_CLASSES[isPinned],
												getStuckClass(isPinned),
												headerCellClassName
											)}
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
										</TableHead>
									);
								})}
							</TableHeadRow>
						))}
					</TableHeader>

					<TableBody className={cj(isEmpty && 'h-full')}>
						{isEmpty ? (
							<tr className="h-full bg-container-bg">
								<TableCell
									colSpan={columns.length}
									className="h-full text-center typo-t3 text-text-secondary"
								>
									{emptyMessage}
								</TableCell>
							</tr>
						) : (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									onClick={
										onRowClick ? () => onRowClick(row.original) : undefined
									}
									className={cj(onRowClick && 'cursor-pointer')}
								>
									{row.getVisibleCells().map((cell) => {
										const isPinned = cell.column.getIsPinned();
										const bodyCellClassName =
											cell.column.columnDef.meta?.bodyCellClassName;
										return (
											<TableCell
												key={cell.id}
												style={getColumnStyle(cell.column)}
												className={cm(
													isPinned && PINNED_COLUMN_CLASSES[isPinned],
													getStuckClass(isPinned),
													bodyCellClassName
												)}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</TableCell>
										);
									})}
								</TableRow>
							))
						)}
					</TableBody>

					{footer && (
						<TableFooter>
							<TableRow className="hover:bg-container-bg">{footer}</TableRow>
						</TableFooter>
					)}
				</Table>
			</div>

			{showPaginator && pagination && (
				<Paginator
					className="rounded-b-sm"
					// Note, this defaults to true but can be overridden via `pagination`.
					isTokenBasedPagination
					{...pagination}
				/>
			)}
		</div>
	);
}
